import type { PageViewBox } from "./highlights";

/** Where a rendered page's highlights mount, and how the page is drawn. */
export type PageLayer = { host: HTMLDivElement; viewBox: PageViewBox; rotation: number };

export type PageLayers = ReadonlyMap<number, PageLayer>;

const NO_LAYERS: PageLayers = new Map();

const sameViewBox = (a: PageViewBox, b: PageViewBox) =>
	a.length === b.length && a.every((value, index) => value === b[index]);

const sameLayer = (a: PageLayer, b: PageLayer) =>
	a.host === b.host && a.rotation === b.rotation && sameViewBox(a.viewBox, b.viewBox);

/**
 * `current` with `attached` merged in. Hands back `current` itself when no layer changed, so a
 * page that re-renders at the same size and rotation doesn't re-render the highlights.
 */
export const withLayers = (current: PageLayers, attached: PageLayers): PageLayers => {
	let next: Map<number, PageLayer> | undefined;
	for (const [pageNumber, layer] of attached) {
		const previous = current.get(pageNumber);
		if (previous && sameLayer(previous, layer)) continue;
		next ??= new Map(current);
		next.set(pageNumber, layer);
	}
	return next ?? current;
};

/**
 * Each rendered page's highlight layer, held outside React state. Drawing a page while the
 * reader scrolls then re-renders only what reads the store through `useSyncExternalStore` (the
 * highlights), not the whole viewer.
 */
export type LayerStore = {
	subscribe: (onChange: () => void) => () => void;
	getSnapshot: () => PageLayers;
	/** Merges the layers in, telling subscribers only when one of them changed. */
	attach: (attached: PageLayers) => void;
	clear: () => void;
};

export const createLayerStore = (): LayerStore => {
	let layers = NO_LAYERS;
	const listeners = new Set<() => void>();
	const publish = (next: PageLayers) => {
		if (next === layers) return;
		layers = next;
		for (const listener of listeners) listener();
	};
	return {
		subscribe: (onChange) => {
			listeners.add(onChange);
			return () => {
				listeners.delete(onChange);
			};
		},
		getSnapshot: () => layers,
		attach: (attached) => publish(withLayers(layers, attached)),
		clear: () => publish(NO_LAYERS),
	};
};
